import React from 'react'
import { PAYMENT_TYPE, PAYMENT_TYPE_PARAMS } from '../components/PaymentMethodSelection/PaymentMethodSelection'
import { LocalStorageManager, SessionStorageManager } from '../helper/localStorage'
import {
  PARAM_APIKEY,
  PARAM_HEADER_TITLE,
  PARAM_IS_EMBEDDED_VIEW,
  PARAM_IS_HEADER_MINI_APP,
  PARAM_IS_WEB_VIEW,
  PARAM_PAYMENT_METHOD,
  PARAM_REFER_STATION_ID,
  PARAM_REFER_USER_ID,
  PARAM_APP_THEME_NAME
} from '../constants/params'
import { getUrlParamValue, smartParseParam } from '../helper/params'
export const AppParamsContext = React.createContext(null)

const resolveParamsMiniAppSessionStorage = (config) => {
  // 1. param (raw)
  const param = getUrlParamValue(config.paramKey)

  // có param thì lưu session (raw)
  if (param !== null && param !== undefined && config.storageKey) {
    SessionStorageManager.setItem(config.storageKey, param)
  }

  // 2. resolve raw theo thứ tự ưu tiên
  const raw = param ?? SessionStorageManager.getItem(config.storageKey) ?? smartParseParam(process.env?.[config.envKey]) ?? config.defaultValue

  // 3. parse giá trị cuối cùng
  return config.parser ? config.parser(raw) : raw
}

const resolveParamsMiniAppLocalStorage = (config) => {
  // 1. param (raw)
  const param = getUrlParamValue(config.paramKey)

  // có param thì lưu local (raw)
  if (param !== null && param !== undefined && config.storageKey) {
    LocalStorageManager.setItem(config.storageKey, param)
  }

  // 2. resolve raw theo thứ tự ưu tiên
  const raw = param ?? LocalStorageManager.getItem(config.storageKey) ?? smartParseParam(process.env?.[config.envKey]) ?? config.defaultValue

  // 3. parse giá trị cuối cùng
  return config.parser ? config.parser(raw) : raw
}

/* =====================
 * PARAM MINI APP SCHEMA
 * ===================== */
const PARAMS_MINIAPP_SCHEMA_SESSION_STORAGE = {
  // apikey: {
  //   paramKey: PARAM_APIKEY,
  //   storageKey: PARAM_APIKEY,
  //   envKey: 'REACT_APP_APIKEY',
  //   defaultValue: undefined,
  //   parser: (v) => v
  // },
  appThemeName: {
    paramKey: PARAM_APP_THEME_NAME,
    storageKey: PARAM_APP_THEME_NAME,
    envKey: 'REACT_APP_THEME_NAME',
    defaultValue: undefined,
    parser: (v) => v
  },
  isEmbeddedView: {
    paramKey: PARAM_IS_EMBEDDED_VIEW,
    storageKey: PARAM_IS_EMBEDDED_VIEW,
    envKey: 'REACT_APP_PARAM_IS_EMBEDDED_VIEW',
    defaultValue: false,
    parser: (v) => v === true || v === 1
  },
  isHeaderMiniApp: {
    paramKey: PARAM_IS_HEADER_MINI_APP,
    storageKey: PARAM_IS_HEADER_MINI_APP,
    envKey: 'REACT_APP_PARAM_IS_HEADER_MINI_APP',
    defaultValue: undefined,
    parser: (v) => v
  },
  headerTitle: {
    paramKey: PARAM_HEADER_TITLE,
    storageKey: PARAM_HEADER_TITLE,
    envKey: undefined,
    defaultValue: undefined,
    parser: (v) => v
  },
  isWebView: {
    paramKey: PARAM_IS_WEB_VIEW,
    storageKey: PARAM_IS_WEB_VIEW,
    envKey: undefined,
    defaultValue: undefined,
    parser: (v) => v === true || v === 1
  },
  paymentMethodType: {
    paramKey: PARAM_PAYMENT_METHOD,
    storageKey: PARAM_PAYMENT_METHOD,
    envKey: 'REACT_APP_PAYMENT_METHOD',
    defaultValue: undefined,

    parser: (v) => {
      const paymentMethod = (v || '').toLowerCase()
      const key = Object.keys(PAYMENT_TYPE_PARAMS).find((k) => PAYMENT_TYPE_PARAMS[k].some((item) => item.toLowerCase() === paymentMethod))
      return key ? PAYMENT_TYPE[key] : undefined
    }
  }
}

const PARAMS_MINIAPP_SCHEMA_LOCAL_STORAGE = {
  // Cờ cấu hình bật giao diện trang chủ Saladin (UI mới) hay mặc định (UI cũ)
  // Nhận giá trị true hoặc 1 để hiển thị UI Saladin
  isHomeUISaladin: {
    paramKey: 'isHomeUISaladin',
    storageKey: 'isHomeUISaladin',
    envKey: 'REACT_APP_IS_HOME_UI_SALADIN',
    defaultValue: false,
    parser: (v) => v === true || v === 1 || String(v).toLowerCase() === 'true' || String(v) === '1'
  },
  referUserId: {
    paramKey: PARAM_REFER_USER_ID,
    storageKey: PARAM_REFER_USER_ID,
    envKey: undefined,
    defaultValue: undefined,
    parser: (v) => v
  },
  referStationId: {
    paramKey: PARAM_REFER_STATION_ID,
    storageKey: PARAM_REFER_STATION_ID,
    envKey: undefined,
    defaultValue: false,
    parser: (v) => v
  }
}

export const AppParamsContextProvider = (props) => {
  const paramsMiniAppSessionStorage = React.useMemo(() => {
    const params = {}

    Object.keys(PARAMS_MINIAPP_SCHEMA_SESSION_STORAGE).forEach((key) => {
      params[key] = resolveParamsMiniAppSessionStorage(PARAMS_MINIAPP_SCHEMA_SESSION_STORAGE[key])
    })

    return params
  }, [])

  const paramsMiniAppLocalStorage = React.useMemo(() => {
    const params = {}

    Object.keys(PARAMS_MINIAPP_SCHEMA_LOCAL_STORAGE).forEach((key) => {
      params[key] = resolveParamsMiniAppLocalStorage(PARAMS_MINIAPP_SCHEMA_LOCAL_STORAGE[key])
    })

    return params
  }, [])

  // pass resolved params through context value
  return React.createElement(
    AppParamsContext.Provider,
    {
      value: {
        ...paramsMiniAppSessionStorage,
        ...paramsMiniAppLocalStorage
      }
    },
    props.children
  )
}
export const useAppParamsContext = () => {
  const context = React.useContext(AppParamsContext)
  if (!context) {
    throw new Error('useAppParamsContext must be used within AppParamsContextProvider')
  }
  return context
}
